import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, takeUntil } from 'rxjs';
import { ReportService, Report, ReportsListResponse } from '../services/report.service';
import { KundeService } from '../services/kunde.service';
import { StandortService } from '../services/standort.service';
import { PaginationService } from '../core/services/pagination.service';
import { LocalStorageFilterService } from '../core/services/local-storage-filter.service';

export interface FilterOptions {
  client_id?: string;
  standort_id?: string;
  date_from?: string;
  date_to?: string;
  status?: string;
  category?: string;
}

@Component({
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.scss']
})
export class ReportsComponent implements OnInit, OnDestroy {
  displayedColumns: string[] = ['einsatznummer', 'client_name', 'standort_name', 'techniker_name', 'created_at', 'status', 'version', 'actions'];
  dataSource: MatTableDataSource<Report>;
  
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  private destroy$ = new Subject<void>();
  
  loading = false;
  totalItems = 0;
  currentPage = 1;
  pageIndex = 0; // 0-based page index for mat-paginator
  pageSize: number;
  pageSizeOptions: number[];
  searchTerm = '';
  
  // Filter options
  filters: FilterOptions = {};
  clients: any[] = [];
  standorte: any[] = [];
  
  // Autocomplete search values
  kundeSearchValue = '';
  standortSearchValue = '';
  
  // Selected filter objects (instead of just IDs)
  selectedKunde: any = null;
  selectedStandort: any = null;
  
  // Autocomplete visibility flags
  showKundeAutocomplete = false;
  showStandortAutocomplete = false;
  statusDropdownOpen = false;
  
  // Filtered autocomplete results
  filteredKunden: any[] = [];
  filteredStandorte: any[] = [];
  
  // Action dropdown state
  openDropdownId: string | null = null;
  
  // Status options
  statusOptions = [
    { value: '', label: 'Alle Status' },
    { value: 'generated', label: 'Generiert' },
    { value: 'sent', label: 'Versendet' }
  ];

  // LocalStorage keys
  private readonly STORAGE_KEY_SEARCH = 'berichte_search_history';
  private readonly STORAGE_KEY_FILTERS = 'berichte_filters';
  private readonly STORAGE_KEY_PAGINATION = 'berichte_pagination';
  searchHistory: string[] = [];

  constructor(
    private snackBar: MatSnackBar,
    private router: Router,
    private reportService: ReportService,
    private kundeService: KundeService,
    private standortService: StandortService,
    private paginationService: PaginationService,
    private localStorageService: LocalStorageFilterService
  ) {
    this.dataSource = new MatTableDataSource<Report>([]);
    // Initialize pagination from service
    this.pageSize = this.paginationService.getPageSize();
    this.pageSizeOptions = this.paginationService.getPageSizeOptions();
  }

  ngOnInit(): void {
    console.log('✅ ReportsComponent ngOnInit - Component loaded successfully');
    console.log('📍 Current URL:', window.location.href);
    console.log('🎯 Component should be loaded within shell');
    
    // Load from localStorage first
    this.loadFromLocalStorage();
    
    this.loadFilterOptions();
    this.loadReports();
    
    // Add click outside listener to close dropdown
    document.addEventListener('click', this.onDocumentClick.bind(this));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    // Remove click outside listener
    document.removeEventListener('click', this.onDocumentClick.bind(this));
  }

  onDocumentClick(event: Event): void {
    // Close status dropdown when clicking outside
    this.statusDropdownOpen = false;
    // Close action dropdown when clicking outside
    this.openDropdownId = null;
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  /**
   * Load search history and filters from localStorage
   */
  loadFromLocalStorage(): void {
    // Load search history
    this.searchHistory = this.localStorageService.getSearchHistory(this.STORAGE_KEY_SEARCH);

    // Load filters
    const savedFilters = this.localStorageService.loadFilters(this.STORAGE_KEY_FILTERS);
    if (savedFilters) {
      // Restore filter state
      if (savedFilters.client_id) {
        this.filters.client_id = savedFilters.client_id;
      }
      if (savedFilters.standort_id) {
        this.filters.standort_id = savedFilters.standort_id;
      }
      if (savedFilters.status) {
        this.filters.status = savedFilters.status;
      }
      if (savedFilters.category) {
        this.filters.category = savedFilters.category;
      }
      if (savedFilters.searchTerm) {
        this.searchTerm = savedFilters.searchTerm;
      }
    }

    // Load pagination
    const savedPagination = this.localStorageService.loadPagination(this.STORAGE_KEY_PAGINATION);
    if (savedPagination) {
      this.currentPage = savedPagination.currentPage || 1;
      this.pageSize = savedPagination.pageSize || this.pageSize;
      this.pageIndex = this.currentPage - 1;
    }
  }

  /**
   * Save current filters to localStorage
   */
  saveFiltersToLocalStorage(): void {
    const filters = {
      client_id: this.filters.client_id || null,
      standort_id: this.filters.standort_id || null,
      status: this.filters.status || null,
      category: this.filters.category || null,
      searchTerm: this.searchTerm || ''
    };
    this.localStorageService.saveFilters(this.STORAGE_KEY_FILTERS, filters);
  }

  /**
   * Save pagination state to localStorage
   */
  savePaginationToLocalStorage(): void {
    this.localStorageService.savePagination(this.STORAGE_KEY_PAGINATION, this.currentPage, this.pageSize);
  }

  loadFilterOptions(): void {
    // Load all clients (use high limit to get all)
    this.kundeService.getKunden(1, 10000).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        this.clients = response.data || [];
        
        // If filter has client_id, find and set selectedKunde
        if (this.filters.client_id) {
          this.selectedKunde = this.clients.find(c => c.id === this.filters.client_id);
          if (this.selectedKunde) {
            this.kundeSearchValue = this.selectedKunde.suchbegriff || this.selectedKunde.name || '';
          }
        }
      },
      error: (error) => {
        console.error('Error loading clients:', error);
      }
    });

    // Load all standorte (use high limit to get all)
    this.standortService.getStandorte(1, 10000).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        this.standorte = response.data || [];
        
        // If filter has standort_id, find and set selectedStandort
        if (this.filters.standort_id) {
          this.selectedStandort = this.standorte.find(s => s.id === this.filters.standort_id);
          if (this.selectedStandort) {
            this.standortSearchValue = this.selectedStandort.name || '';
          }
        }
      },
      error: (error) => {
        console.error('Error loading standorte:', error);
      }
    });
  }

  loadReports(): void {
    this.loading = true;
    this.pageIndex = this.currentPage - 1; // Convert 1-based to 0-based for mat-paginator
    
    const filters = {
      ...this.filters,
      search: this.searchTerm || undefined,
      page: this.currentPage,
      limit: this.pageSize
    };
    
    this.reportService.getReports(filters).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response: ReportsListResponse) => {
        this.dataSource.data = response.reports;
        this.totalItems = response.pagination.total;
        this.loading = false;
        
        // Update paginator page index if it exists
        if (this.paginator) {
          this.paginator.pageIndex = this.pageIndex;
        }
        
        console.log('✅ Reports loaded:', response.reports.length);
      },
      error: (error) => {
        this.loading = false;
        this.snackBar.open('Fehler beim Laden der Berichte', 'Schließen', { duration: 3000 });
        console.error('Error loading reports:', error);
      }
    });
  }

  onSearch(): void {
    // Save to search history
    if (this.searchTerm && this.searchTerm.trim()) {
      this.localStorageService.saveSearchToHistory(this.STORAGE_KEY_SEARCH, this.searchTerm);
      this.searchHistory = this.localStorageService.getSearchHistory(this.STORAGE_KEY_SEARCH);
    }
    
    // Save filters
    this.saveFiltersToLocalStorage();
    
    // For now, just reload with current filters
    this.currentPage = 1;
    this.pageIndex = 0;
    this.savePaginationToLocalStorage();
    this.loadReports();
  }

  /**
   * Get status label for display
   */
  getStatusLabel(): string {
    if (!this.filters.status || this.filters.status === '') {
      return 'Alle Status';
    } else if (this.filters.status === 'generated') {
      return 'Generiert';
    } else if (this.filters.status === 'sent') {
      return 'Versendet';
    }
    return 'Alle Status';
  }

  /**
   * Toggle status dropdown
   */
  toggleStatusDropdown(event: Event): void {
    event.stopPropagation();
    this.statusDropdownOpen = !this.statusDropdownOpen;
  }

  /**
   * Handle status change
   */
  onStatusChange(statusValue: string): void {
    this.filters.status = statusValue === '' ? undefined : statusValue;
    this.statusDropdownOpen = false;
    this.saveFiltersToLocalStorage();
    this.onFilterChange();
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.pageIndex = 0;
    this.saveFiltersToLocalStorage();
    this.savePaginationToLocalStorage();
    this.loadReports();
  }

  clearFilters(): void {
    this.filters = {};
    this.searchTerm = '';
    
    // Clear autocomplete selections
    this.selectedKunde = null;
    this.selectedStandort = null;
    this.kundeSearchValue = '';
    this.standortSearchValue = '';
    
    this.currentPage = 1;
    this.pageIndex = 0;
    this.loadReports();
  }

  onPageChange(event: any): void {
    this.currentPage = event.pageIndex + 1;
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.paginationService.setPageSize(event.pageSize);
    this.savePaginationToLocalStorage();
    this.loadReports();
  }

  viewReport(report: Report): void {
    if (!report.uuid) {
      this.snackBar.open('Fehler: Bericht UUID nicht gefunden', 'Schließen', { duration: 3000 });
      return;
    }
    this.reportService.viewReportHtmlById(report.uuid);
  }

  downloadReport(report: Report): void {
    this.reportService.downloadReportById(report.uuid);
  }

  viewKontrolltermin(report: Report): void {
    // Navigate to kontrolltermin in abgeschlossen status since a report exists
    this.router.navigate(['/kontrolltermine', report.kontrolltermin_id], {
      queryParams: { 
        mode: 'view', 
        sourceStatus: 'abgeschlossen' 
      }
    });
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'sent':
        return 'bg-green-50 text-green-700 ring-green-600/20';
      case 'generated':
        return 'bg-blue-50 text-blue-700 ring-blue-600/20';
      default:
        return 'bg-gray-50 text-gray-700 ring-gray-600/20';
    }
  }

  getStatusText(status: string): string {
    switch (status) {
      case 'sent':
        return 'Versendet';
      case 'generated':
        return 'Generiert';
      default:
        return 'Unbekannt';
    }
  }

  formatFileSize(bytes: number): string {
    return this.reportService.formatFileSize(bytes);
  }

  formatDate(dateString: string): string {
    return this.reportService.formatDate(dateString);
  }

  /**
   * Format date only (without time)
   */
  formatDateOnly(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  }

  /**
   * Format time only with "Uhr" suffix
   */
  formatTimeOnly(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit'
    }) + ' Uhr';
  }

  // Kunde Autocomplete Methods
  onKundeSearch(event: any): void {
    const value = event.target.value;
    this.kundeSearchValue = value;
    
    if (value.length > 0) {
      this.filteredKunden = this.clients.filter(client => 
        client.suchbegriff?.toLowerCase().includes(value.toLowerCase()) ||
        client.name?.toLowerCase().includes(value.toLowerCase())
      );
    } else {
      // Show all clients when search is empty
      this.filteredKunden = this.clients;
    }
  }

  onKundeBlur(): void {
    setTimeout(() => {
      this.showKundeAutocomplete = false;
    }, 200);
  }

  onKundeFocus(): void {
    // Show all clients when focusing on the input
    this.filteredKunden = this.clients;
    this.showKundeAutocomplete = true;
  }

  selectKunde(kunde: any): void {
    this.selectedKunde = kunde;
    this.kundeSearchValue = kunde.suchbegriff || kunde.name || '';
    this.showKundeAutocomplete = false;
    this.filters.client_id = kunde.id; // Update filter
    this.saveFiltersToLocalStorage();
    this.onFilterChange();
  }

  clearKundeFilter(): void {
    this.selectedKunde = null;
    this.kundeSearchValue = '';
    this.filters.client_id = undefined;
    this.saveFiltersToLocalStorage();
    this.onFilterChange();
  }

  getKundeDisplayValue(): string {
    if (this.selectedKunde) {
      return this.selectedKunde.suchbegriff || this.selectedKunde.name || '';
    }
    return this.kundeSearchValue;
  }

  // Standort Autocomplete Methods
  onStandortSearch(event: any): void {
    const value = event.target.value;
    this.standortSearchValue = value;
    
    if (value.length > 0) {
      this.filteredStandorte = this.standorte.filter(standort => 
        standort.name?.toLowerCase().includes(value.toLowerCase()) ||
        standort.nummer?.toString().includes(value)
      );
    } else {
      // Show all standorte when search is empty
      this.filteredStandorte = this.standorte;
    }
  }

  onStandortBlur(): void {
    setTimeout(() => {
      this.showStandortAutocomplete = false;
    }, 200);
  }

  onStandortFocus(): void {
    // Show all standorte when focusing on the input
    this.filteredStandorte = this.standorte;
    this.showStandortAutocomplete = true;
  }

  selectStandort(standort: any): void {
    this.selectedStandort = standort;
    this.standortSearchValue = standort.name || '';
    this.showStandortAutocomplete = false;
    this.filters.standort_id = standort.id; // Update filter
    this.saveFiltersToLocalStorage();
    this.onFilterChange();
  }

  clearStandortFilter(): void {
    this.selectedStandort = null;
    this.standortSearchValue = '';
    this.filters.standort_id = undefined;
    this.saveFiltersToLocalStorage();
    this.onFilterChange();
  }

  getStandortDisplayValue(): string {
    if (this.selectedStandort) {
      return this.selectedStandort.name || '';
    }
    return this.standortSearchValue;
  }

  /**
   * Get client name for display (similar to standort component)
   */
  getClientName(report: Report): string {
    // Use client_name which is always available
    return report.client_name || '-';
  }

  /**
   * Get report category label
   */
  getReportCategoryLabel(category?: string): string {
    if (!category) {
      return 'Standard Bericht';
    }
    const categories = this.reportService.getAvailableReportCategories();
    const categoryOption = categories.find(c => c.value === category);
    return categoryOption?.label || category;
  }

  /**
   * Toggle action dropdown
   */
  toggleActionDropdown(reportId: string, event: Event): void {
    event.stopPropagation();
    if (this.openDropdownId === reportId) {
      this.openDropdownId = null;
    } else {
      this.openDropdownId = reportId;
    }
  }
}
